import type { ProjectInventoryResponse } from '@detachly/contracts'
import { useState } from 'react'
import { Button, Card } from './ui'

type Scan = NonNullable<ProjectInventoryResponse['scan']>

/**
 * What the last scan found, spelled out: the routes a build would produce and the shape of every
 * collection. Field names and types only — item values live in the Content Pack, and nothing here
 * is inferred from anything but the snapshot the runner reported (F13).
 */
export function InventoryDetail({ scan }: { scan: Scan }) {
  const [openCollectionId, setOpenCollectionId] = useState<string | null>(null)
  const collectionName = new Map(
    scan.collections.map((collection) => [collection.id, collection.name]),
  )

  return (
    <Card flat>
      <div className="card__title">
        <h2>What the scan found</h2>
      </div>

      <h3 className="subtle">Pages</h3>
      {scan.pages.length === 0 ? (
        <p className="muted">This scan recorded no pages.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Path</th>
              <th scope="col">Kind</th>
              <th scope="col">State</th>
            </tr>
          </thead>
          <tbody>
            {scan.pages.map((page) => (
              <tr key={page.id}>
                <td className="mono">{page.path ?? '—'}</td>
                <td className="muted">
                  {page.collectionId
                    ? `CMS · ${collectionName.get(page.collectionId) ?? page.collectionId}`
                    : 'Static'}
                </td>
                <td className="muted">
                  {page.draft ? 'Draft' : page.path === null ? 'No route' : 'Published'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className="subtle">Collections</h3>
      {scan.collections.length === 0 ? (
        <p className="muted">This project has no CMS collections.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Collection</th>
              <th scope="col">Slug field</th>
              <th scope="col">Fields</th>
              <th scope="col">Items</th>
              <th scope="col" className="table__actions">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {scan.collections.map((collection) => {
              const open = openCollectionId === collection.id
              return (
                <tr key={collection.id}>
                  <td>
                    <div className="step__name">{collection.name}</div>
                    {open ? (
                      <ul className="help-list" id={`fields-${collection.id}`}>
                        {collection.fields.map((field) => (
                          <li key={field.id}>
                            <span className="mono">{field.name}</span>{' '}
                            <span className="subtle">{field.type}</span>
                          </li>
                        ))}
                        {collection.fields.length === 0 ? (
                          <li className="subtle">
                            This scan ran before field names were recorded — re-scan to see them.
                          </li>
                        ) : null}
                      </ul>
                    ) : null}
                  </td>
                  <td className="muted mono">{collection.slugFieldName ?? '—'}</td>
                  <td className="muted">{collection.fieldCount}</td>
                  <td className="muted">
                    {collection.itemCount}
                    {collection.draftItemCount > 0 ? ` · ${collection.draftItemCount} draft` : ''}
                  </td>
                  <td className="table__actions">
                    <Button
                      variant="secondary"
                      aria-expanded={open}
                      aria-controls={`fields-${collection.id}`}
                      // Every row says "Show fields"; the collection name is what tells them apart.
                      aria-label={`${open ? 'Hide' : 'Show'} fields for ${collection.name}`}
                      onClick={() => setOpenCollectionId(open ? null : collection.id)}
                    >
                      {open ? 'Hide fields' : 'Show fields'}
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </Card>
  )
}
